import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { usePersistExistingPatientReducer } from 'reducer/existingUser';
import OutlineButton from 'components/common/outlineButton';
import {
  ConditionalOptionsExistingType,
  OriginalSymptomsReactionType,
} from 'types/existingPatients';
import styles from 'components/existingPatient/page.module.scss';
import MetaTag from 'components/meta';
import { StateType } from 'reducer';
import { forEach } from 'lodash';
import Label from 'components/common/label';
import Button from 'components/common/button';
import clsx from 'clsx';

enum ReactionName {
  Yes = 'Yes',
  No = 'No',
}

type Props = {
  options: ConditionalOptionsExistingType[];
  nextPage: number;
  currentPage?: number;
  actionPage: string;
  multiple?: boolean;
  isFilter?: boolean;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function HomeActivities({
  options,
  nextPage,
  nextOrSubmitButton,
  actionPage,
  multiple = true,
  isFilter = false,
}: Props) {
  const [inputText, setInputText] = useState('');
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState<string | null>('');
  const [questionOptions, setQuestionOptions] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string[]>([]);
  const [selectedHomeActivitiesOption, setSelectedHomeActivitiesOption] = useState<
  OriginalSymptomsReactionType[]
  >([]);
  const [error, setError] = useState<string>('');
  const [state, dispatch] = usePersistExistingPatientReducer();

  useEffect(() => {
    if (isFilter) {
      const getQuestion = options.find(
        (obj: ConditionalOptionsExistingType) => obj.page === actionPage,
      );
      if (getQuestion) {
        setQuestionOptions(getQuestion.options);
        setTitle(getQuestion.title);
        setSubTitle(getQuestion.subTitle);
      }
    }
  }, []);

  useEffect(() => {
    if (state) {
      const data = state.find((object: StateType) => object.name === actionPage);
      if (data) {
        const option: string[] = [];
        forEach(data.value, (i) => {
          option.push(i.name);
        });
        setSelectedOption(option);
        setSelectedHomeActivitiesOption(data.value);
      }
    } else if (selectedOption.length === 0) {
      setSelectedHomeActivitiesOption([]);
    }
  }, []);

  const onSelectOptions = useCallback((item: string) => {
    if (multiple) {
      setSelectedOption((old) => {
        if (old.find((value) => value === item)) {
          return old.filter((data) => data !== item);
        } return [...old, item];
      });
    } else {
      setSelectedOption([item]);
    }

    setError('');
  }, []);

  const clickNextOrSubmit = async (nextPage: number) => {
    if (selectedOption.length === 0) {
      setError('Please select at least one option.');
      return;
    }
    forEach(selectedOption, (item) => {
      const findSubItem = selectedHomeActivitiesOption.find(
        (obj) => obj.name === item,
      );
      if (!findSubItem || findSubItem?.value === '') {
        setError("Please select 'Is this helping?' option");
      }
    });

    const dispatchPayload = {
      type: actionPage,
      name: title,
      value: selectedHomeActivitiesOption,
    };

    await dispatch(dispatchPayload);
    nextOrSubmitButton(nextPage, true);
  };

  const onChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
  };

  const onClickSymptomsStatus = (name: string, value: string) => {
    setSelectedHomeActivitiesOption((prev: OriginalSymptomsReactionType[]) => {
      const ifExisting = prev.find((e) => e.name === name);
      if (ifExisting) {
        const filterByRemove = prev.filter((e) => e.name !== name);
        return [...filterByRemove, { name, value }];
      }
      return [...prev, { name, value }];
    });
    setError('');
  };

  return (
    <>
      <MetaTag title={title} />
      <h2 className={`marginTopZero ${styles.titleText}`}>{title}</h2>
      {subTitle && <p className={styles.titleTextSubText}>{subTitle}</p>}
      {error && <p className="error-message">{error}</p>}

      <div>
        {questionOptions.map((option) => {
          const selectedClass = selectedOption.find(
            (item) => item === option,
          );
          const selectedReaction = selectedHomeActivitiesOption.find(
            (item) => item.name === option,
          );
          return (
            <Box key={option}>
              <p
                className={clsx('option', { 'selected-option': selectedClass })}
                onClick={() => onSelectOptions(option)}
                onKeyUp={() => {}}
              >
                {option}
              </p>
              {selectedClass && (
              <>
                <Label>
                  Is this helping?
                </Label>
                <p className={styles.separator} />
                <div className="flex gap-2 my-2">
                  <OutlineButton
                    onClick={() => onClickSymptomsStatus(option, ReactionName.Yes)}
                    isActive={
                            selectedReaction?.value === ReactionName.Yes
                          }
                  >
                    {ReactionName.Yes}
                  </OutlineButton>
                  <OutlineButton
                    onClick={() => onClickSymptomsStatus(option, ReactionName.No)}
                    isActive={selectedReaction?.value === ReactionName.No}
                  >
                    {ReactionName.No}
                  </OutlineButton>
                </div>
                <p className={styles.separator} />
              </>
              )}
            </Box>
          );
        })}
        {selectedOption.toString() === 'Other' && (
        <TextField
          value={inputText}
          placeholder="Type..."
          className="w-full"
          sx={{
            '& .MuiInputBase-root': { borderRadius: '11px' },
            marginTop: 3,
          }}
          size="small"
          focused
          onChange={onChangeText}
        />
        )}
      </div>
      <div className="next-button-fixed">
        <Button onClick={() => clickNextOrSubmit(nextPage)}>
          Submit
        </Button>
      </div>
    </>
  );
}

export default HomeActivities;
